import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Loader2, ArrowLeft, BookOpen, ChevronRight, Clock,
  Folder, Tablet, Box, Tag, Layers, Archive 
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping
const ICONS = {
  folder: Folder,
  tablet: Tablet,
  box: Box,
  tag: Tag,
  layers: Layers,
  archive: Archive,
};

export default function PublicCardGuidesPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCardGuides = async () => {
      try {
        const res = await fetch(`${API}/public/cards/${slug}`);
        if (!res.ok) {
          if (res.status === 404) {
            throw new Error("Categoria non trovata");
          }
          throw new Error("Errore nel caricamento");
        }
        const result = await res.json();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCardGuides();
  }, [slug]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString('it-IT', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return "";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white/50" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center gap-4">
        <div className="text-center text-white/70">
          <p className="text-xl mb-2">Errore</p>
          <p className="text-sm">{error}</p>
        </div>
        <Button variant="outline" onClick={() => navigate("/")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Torna alla home
        </Button>
      </div>
    );
  }

  const { card, guides } = data;
  const IconComponent = ICONS[card.icon] || Folder;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/")}
              className="text-white/60 hover:text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: card.color + '25' }}
            >
              <IconComponent 
                className="w-6 h-6" 
                style={{ color: card.color }}
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{card.name}</h1>
              {card.description && (
                <p className="text-sm text-white/60">{card.description}</p>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-white/50 mb-6">
          <Link to="/" className="hover:text-white/70 transition-colors">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-white/70">{card.name}</span>
        </nav>

        {guides.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 mx-auto text-white/30 mb-4" />
            <h2 className="text-xl font-semibold text-white/70 mb-2">Nessuna guida in questa categoria</h2>
            <p className="text-white/50 mb-6">Le guide saranno disponibili a breve.</p>
            <Button variant="outline" onClick={() => navigate("/")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna alla home
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-white/60 text-sm mb-4">
              {guides.length} {guides.length === 1 ? 'guida disponibile' : 'guide disponibili'}
            </p>
            
            {guides.map((guide) => (
              <Card
                key={guide.id}
                className="group cursor-pointer bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-200"
                onClick={() => navigate(`/g/${guide.slug}?t=${guide.token}`)}
                data-testid={`guide-${guide.slug}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-center gap-4">
                    {/* Icon */}
                    <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center shrink-0 group-hover:bg-white/10 transition-colors">
                      <BookOpen className="w-6 h-6 text-white/50 group-hover:text-white/70" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-medium text-white group-hover:text-white truncate mb-1">
                        {guide.title || guide.model_name}
                      </h3>
                      {guide.intro && (
                        <p className="text-sm text-white/50 line-clamp-1">
                          {guide.intro}
                        </p>
                      )}
                      {guide.updated_at && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-white/40">
                          <Clock className="w-3 h-3" />
                          <span>Aggiornata il {formatDate(guide.updated_at)}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Arrow */}
                    <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-white/60 group-hover:translate-x-1 transition-all shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 mt-auto">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center text-white/40 text-sm">
          © {new Date().getFullYear()} Sistemi Digitali Group
        </div>
      </footer>
    </div>
  );
}
